import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import ParticleField from "../components/ParticleField";
import { BRAND } from "../lib/brand";
import VideoCard from "../components/VideoCard";
import PinnedVideo from "../components/PinnedVideo";
import PlatformButtons from "../components/PlatformButtons";
import { EmptyVideos } from "./Watch";
import { CATEGORIES } from "../data/resources";
import { VideoCardSkeleton, MessageSkeleton, ReflectionSkeleton } from "../components/Skeleton";
import { container, item } from "../lib/motion";
import {
  Users, RefreshCw, ArrowRight, Quote, Heart,
  Dumbbell, Fingerprint, Brain, HeartPulse, Sparkle, Compass,
  Briefcase, Timer, Wind, Maximize, Mail,
} from "lucide-react";

const HERO_BG = BRAND.hero;

const VALUES = [
  { icon: Dumbbell, label: "Discipline", desc: "The bridge between who you are and who you could be." },
  { icon: Fingerprint, label: "Identity", desc: "Remember the self the world tried to make you forget." },
  { icon: Brain, label: "Psychology", desc: "Understand your mind to finally master it." },
  { icon: HeartPulse, label: "Healing", desc: "Quiet, unglamorous, sacred work of becoming whole." },
  { icon: Sparkle, label: "Faith", desc: "Trust built in the dark, when nothing makes sense." },
  { icon: Compass, label: "Growth", desc: "Not a destination — a direction you choose daily." },
];

const BUSINESS_PILLS = ["Short-Form Content", "Content Strategy", "AI-Assisted Editing", "Social Support"];

const TOOLS_PREVIEW = [
  { icon: Timer, label: "Meditation", desc: "Guided timers with ambient soundscapes." },
  { icon: Wind, label: "Breathing", desc: "Box, 4-7-8 and calming breath patterns." },
  { icon: Maximize, label: "Focus Mode", desc: "A distraction-free space to go deep." },
];

function Section({ children, className = "", id }) {
  return (
    <motion.section id={id} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-90px" }}
      variants={container} className={`max-w-6xl mx-auto px-6 md:px-10 ${className}`}>
      {children}
    </motion.section>
  );
}

export default function Home() {
  const [videos, setVideos] = useState([]);
  const [quote, setQuote] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [vLoaded, setVLoaded] = useState(false);
  const [hLoaded, setHLoaded] = useState(false);

  const loadQuote = async (random = false) => {
    setRefreshing(true);
    try { const { data } = await api.get(random ? "/quotes/random" : "/quotes/daily"); setQuote(data); } catch {}
    setRefreshing(false);
  };

  useEffect(() => {
    api.get("/watch").then((r) => setVideos(r.data)).catch(() => {}).finally(() => setVLoaded(true));
    api.get("/community").then((r) => setHighlights([...r.data].filter((m) => m.likes > 0).sort((a, b) => b.likes - a.likes).slice(0, 3))).catch(() => {}).finally(() => setHLoaded(true));
    loadQuote(false);
  }, []);

  const pinned = videos.find((v) => v.pinned);
  const latest = videos.filter((v) => v !== pinned).slice(0, 6);

  return (
    <Layout glow="top">
      {/* 1 — HERO (compact so videos are visible right away) */}
      <section className="relative min-h-[62vh] md:min-h-[66vh] flex items-center overflow-hidden -mt-20 pt-28 pb-14" data-testid="hero">
        <div className="absolute inset-0 [mask-image:linear-gradient(to_bottom,black_60%,transparent)]">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover object-top opacity-[0.72]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#090909]/40 via-[#090909]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090909]/90 via-transparent to-[#090909]/90" />
          <motion.div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#4da6ff] rounded-full blur-[170px]"
            animate={{ opacity: [0.06, 0.12, 0.06], scale: [1, 1.08, 1] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
        </div>
        <ParticleField density={80} />

        <motion.div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center" initial="hidden" animate="show" variants={container}>
          <motion.span variants={item} className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#4da6ff] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4da6ff] animate-glow" /> Quiet tools for people rebuilding themselves.
          </motion.span>
          <motion.h1 variants={item} className="font-head text-5xl sm:text-6xl lg:text-[4.75rem] font-light tracking-[-0.03em] leading-[0.96] text-glow">
            Remember<br /><span className="font-semibold">Who You Are.</span>
          </motion.h1>
          <motion.p variants={item} className="text-zinc-400 text-base md:text-xl max-w-2xl mx-auto mt-7 leading-relaxed font-light">
            Daily reminders to build discipline, reclaim your identity, and become the person you were meant to be.
          </motion.p>
          <motion.div variants={item} className="mt-9">
            <PlatformButtons prefix="hero" className="justify-center" />
          </motion.div>
        </motion.div>
      </section>

      {/* 2 — LATEST VIDEOS (primary focus) */}
      <Section className="pt-6 pb-20 scroll-mt-24" id="latest-videos">
        <motion.div variants={item} className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="eyebrow">Latest Videos</span>
            <h2 className="font-head text-3xl md:text-4xl lg:text-5xl font-light mt-3 tracking-tight">Fresh from Facebook &amp; TikTok</h2>
          </div>
          <Link to="/watch" data-testid="home-watch-more-link" className="btn-glass text-sm px-5 py-2.5 flex items-center gap-2 group">
            Watch More <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </motion.div>
        <motion.div key={vLoaded ? "loaded" : "loading"} initial="hidden" animate="show" variants={container}>
          {vLoaded && pinned && <PinnedVideo v={pinned} />}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="home-latest-videos">
            {!vLoaded && [0, 1, 2].map((s) => <VideoCardSkeleton key={s} />)}
            {vLoaded && latest.map((v, i) => <VideoCard key={v.id} v={v} index={i} />)}
          </div>
          {vLoaded && latest.length === 0 && !pinned && <EmptyVideos prefix="home" />}
        </motion.div>
      </Section>

      {/* 2 — TODAY'S REFLECTION */}
      <Section className="py-20">
        <motion.div variants={item} className="text-center mb-14">
          <span className="eyebrow">Today's Reflection</span>
          <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">A reminder for this moment</h2>
        </motion.div>
        {!quote && <ReflectionSkeleton />}
        {quote && <motion.div key={quote?.text} initial={{ opacity: 0, scale: 0.97, filter: "blur(8px)" }} whileInView={{ opacity: 1, scale: 1, filter: "blur(0px)" }} viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="relative max-w-3xl mx-auto glass-card p-12 md:p-16 text-center overflow-hidden" data-testid="daily-reflection-card">
          <div className="absolute -top-16 -right-10 w-52 h-52 bg-[#4da6ff]/15 blur-[80px] rounded-full" />
          <Quote className="w-9 h-9 text-[#4da6ff]/70 mx-auto mb-8" strokeWidth={1.5} />
          <p className="font-head text-3xl md:text-[2.5rem] font-light leading-[1.2] tracking-tight" data-testid="daily-reflection-text">{quote ? quote.text : "..."}</p>
          {quote && (
            <div className="mt-8 flex items-center justify-center gap-3 text-sm text-zinc-500">
              <span className="px-3 py-1 rounded-full border border-[#4da6ff]/20 text-[#4da6ff] text-xs">{quote.category}</span>
              <span className="font-light">— {quote.author}</span>
            </div>
          )}
          <button onClick={() => loadQuote(true)} data-testid="daily-reflection-refresh" className="mt-10 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors group">
            <RefreshCw className={`w-4 h-4 transition-transform ${refreshing ? "animate-spin" : "group-hover:rotate-90"}`} /> Another reflection
          </button>
        </motion.div>}
      </Section>

      {/* 3 — WHY SPIRIT ADMIN EXISTS */}
      <Section className="py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.div variants={item} className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="eyebrow">Why We Exist</span>
            <h2 className="font-head text-4xl lg:text-[3.25rem] font-light mt-4 tracking-tight leading-[1.08]">
              You were never<br /><span className="font-semibold">broken.</span> Only forgotten.
            </h2>
            <p className="text-zinc-400 mt-7 font-light text-lg leading-relaxed">
              Spirit Admin isn't a productivity app. It's a quiet rebellion against the noise that made you forget who you are.
            </p>
            <p className="text-zinc-500 mt-5 font-light leading-relaxed">
              Through cinematic short-form content, daily reflection, and tools for stillness, we help you rebuild discipline, reclaim identity, and walk back toward the person you were always meant to become.
            </p>
            <Link to="/resources" className="inline-flex items-center gap-2 mt-8 text-[#4da6ff] text-sm hover:gap-3 transition-all">Explore the library <ArrowRight className="w-4 h-4" /></Link>
            <div className="relative mt-10 rounded-2xl overflow-hidden hidden lg:block">
              <img src={BRAND.vortex} alt="Spirit Admin" className="w-full h-56 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090909] via-transparent to-transparent" />
              <div className="absolute inset-0 border border-white/10 rounded-2xl" />
            </div>
          </motion.div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <motion.div key={v.label} variants={item} className="glass-card p-7">
                <div className="w-11 h-11 rounded-2xl glass flex items-center justify-center mb-5"><v.icon className="w-5 h-5 text-[#4da6ff]" strokeWidth={1.5} /></div>
                <h3 className="font-head text-lg font-medium">{v.label}</h3>
                <p className="text-zinc-500 text-sm mt-2 font-light leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* 4 — FOR BUSINESS PREVIEW */}
      <Section className="py-24">
        <motion.div variants={item} className="relative glass-card p-10 md:p-14 overflow-hidden" data-testid="business-preview">
          <div className="absolute -top-16 -right-10 w-64 h-64 bg-[#4da6ff]/15 blur-[90px] rounded-full" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="eyebrow">Spirit Admin for Business</span>
              <h2 className="font-head text-3xl lg:text-4xl font-light mt-4 tracking-tight leading-[1.1]">Short-form content that keeps brands seen</h2>
              <p className="text-zinc-400 mt-5 font-light text-lg leading-relaxed">
                Consistent, high-quality short-form content for Facebook, TikTok, and other short-form platforms — planned, edited, and delivered ready to post.
              </p>
              <Link to="/business" data-testid="business-preview-cta" className="btn-primary font-medium px-7 py-3.5 mt-8 inline-flex items-center gap-2">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {BUSINESS_PILLS.map((p) => (
                <div key={p} className="glass rounded-2xl px-5 py-6 flex items-center gap-3">
                  <Briefcase className="w-4 h-4 text-[#4da6ff] shrink-0" strokeWidth={1.5} />
                  <span className="text-sm text-zinc-300 font-light">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </Section>

      {/* 5 — RESOURCE LIBRARY PREVIEW */}
      <Section className="py-20">
        <motion.div variants={item} className="text-center mb-12">
          <span className="eyebrow">Resource Library</span>
          <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Wisdom for the journey</h2>
          <p className="text-zinc-500 mt-5 max-w-xl mx-auto font-light text-lg">Curated reflections on discipline, identity, healing, purpose, faith, and mindfulness.</p>
        </motion.div>
        <motion.div variants={item} className="flex flex-wrap justify-center gap-2.5 mb-10">
          {CATEGORIES.map((c) => (
            <span key={c.name} className="btn-glass px-5 py-2.5 rounded-full text-sm flex items-center gap-2">
              <c.icon className="w-4 h-4" strokeWidth={1.5} style={{ color: c.color }} /> {c.name}
            </span>
          ))}
        </motion.div>
        <motion.div variants={item} className="text-center">
          <Link to="/resources" data-testid="library-preview-cta" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 font-medium">
            Browse Articles <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </Section>

      {/* 6 — TOOLS PREVIEW */}
      <Section className="py-20">
        <motion.div variants={item} className="text-center mb-12">
          <span className="eyebrow">Mindfulness Tools</span>
          <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">Space to be still</h2>
          <p className="text-zinc-500 mt-5 max-w-xl mx-auto font-light text-lg">Meditation timers, guided breathing, and a distraction-free focus mode.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5">
          {TOOLS_PREVIEW.map((t) => (
            <motion.div key={t.label} variants={item} className="glass-card p-8 text-center" data-testid={`tool-preview-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center mx-auto mb-5"><t.icon className="w-5 h-5 text-[#4da6ff]" strokeWidth={1.5} /></div>
              <h3 className="font-head text-lg font-medium">{t.label}</h3>
              <p className="text-zinc-500 text-sm mt-2.5 font-light leading-relaxed">{t.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.div variants={item} className="text-center mt-12">
          <Link to="/tools" data-testid="tools-preview-cta" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 font-medium">
            Explore Tools <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </Section>

      {/* 7 — COMMUNITY PREVIEW */}
      <Section className="py-24">
        <motion.div variants={item} className="text-center mb-14">
          <span className="eyebrow">Community Highlights</span>
          <h2 className="font-head text-4xl lg:text-5xl font-light mt-4 tracking-tight">You are not walking alone</h2>
          <p className="text-zinc-500 mt-5 max-w-xl mx-auto font-light text-lg">Real words from people on the same path. A wall of light for anyone who needs it.</p>
        </motion.div>
        {hLoaded && highlights.length === 0 && (
          <motion.p variants={item} className="text-center text-zinc-500 font-light" data-testid="community-highlights-empty">The wall is waiting for its first light. Be the one who leaves it.</motion.p>
        )}
        <div className="grid md:grid-cols-3 gap-6">
          {!hLoaded && [0, 1, 2].map((s) => <MessageSkeleton key={s} />)}
          {hLoaded && highlights.map((m) => (
            <motion.div key={m.id} variants={item} className="glass-card p-8 flex flex-col">
              <Quote className="w-6 h-6 text-[#4da6ff]/50 mb-4" strokeWidth={1.5} />
              <p className="text-zinc-200 leading-relaxed flex-1 font-light">"{m.message}"</p>
              <div className="flex items-center justify-between mt-6 text-sm">
                <span className="text-zinc-500 font-light">— {m.alias}</span>
                <span className="flex items-center gap-1.5 text-[#fb7185]"><Heart className="w-4 h-4" fill="currentColor" /> {m.likes}</span>
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div variants={item} className="text-center mt-12">
          <Link to="/community" data-testid="community-highlights-cta" className="btn-glass inline-flex items-center gap-2 px-7 py-3.5 font-medium">
            <Users className="w-4 h-4" /> Visit Community
          </Link>
        </motion.div>
      </Section>

      {/* 8 — NEWSLETTER PREVIEW */}
      <Section className="py-20">
        <motion.div variants={item} className="relative glass-card p-12 md:p-16 text-center overflow-hidden" data-testid="newsletter-preview">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4da6ff]/12 via-transparent to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#4da6ff]/10 blur-[120px] rounded-full" />
          <div className="relative z-10 max-w-xl mx-auto">
            <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center mx-auto mb-6"><Mail className="w-6 h-6 text-[#4da6ff]" strokeWidth={1.5} /></div>
            <h2 className="font-head text-4xl lg:text-[3rem] font-light tracking-tight leading-[1.08]">One reminder.<br /><span className="font-semibold">Every morning.</span></h2>
            <p className="text-zinc-400 mt-5 font-light text-lg">Join the Inner Circle for daily reflections, exclusive content, and future member benefits.</p>
            <Link to="/newsletter" data-testid="newsletter-preview-cta" className="btn-primary font-medium px-8 py-3.5 mt-9 inline-flex items-center gap-2.5">
              Join the Inner Circle <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </Section>

    </Layout>
  );
}
